import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useClaims } from "@/hooks/use-claims";
import { useAdminOrgStore } from "@/stores/admin-org";
import { applyBrand, clearBrand } from "@/lib/brand";
import { renderLogoFavicon } from "@/lib/logo-image";
import { useTranslation } from "@/i18n/use-translation";
import { apiError } from "@/lib/api-error";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

export interface OrgBrand {
  name: string;
  primary_color: string | null;
  logo_url: string | null;
  logo_url_expires_at: string | null;
  /** The org rung of the locale chain (POS2-1133) — see `useLocale` for why it arrives here. */
  default_locale: string | null;
}

/** The organisation whose brand the app should be wearing.
 *
 * A platform admin's effective organisation is whatever the context picker last set, which is
 * also what `useApiHeaders` sends as `X-Org-Id` — so keying the query on it is what makes a
 * switch repaint with no reload, and clearing it (or signing out) fall back to FoodSight.
 */
function useEffectiveOrgId(): string {
  const { orgId, isPlatformAdmin } = useClaims();
  const adminOrgId = useAdminOrgStore((s) => s.orgId);
  return isPlatformAdmin && adminOrgId ? adminOrgId : orgId;
}

/** The brand read, shared by every consumer through one query key.
 *
 * `staleTime` sits at half the signed URL's one-hour life, so the `<img src>` is stable across
 * renders and can never be one that has already expired. Deliberately not persisted anywhere:
 * a presigned URL in localStorage would outlive its signature.
 */
export function useBrandQuery() {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const orgId = useEffectiveOrgId();
  const hasToken = !!headers.Authorization.replace("Bearer ", "");

  return useQuery<OrgBrand>({
    queryKey: ["organisation", "brand", orgId],
    enabled: !!orgId && hasToken,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/organisations/me/brand`, { headers });
      if (!res.ok) throw await apiError(t, res);
      const json = await res.json();
      return json.data;
    },
  });
}

/** Paint the brand tokens for as long as this is mounted.
 *
 * Mounted in `AppLayout`, not `App` — the organisation is unknown before authentication, so
 * `/login` and `/waiting` render outside the shell and cannot be branded even by accident. The
 * cleanup is what returns the FoodSight palette on sign-out, when the shell unmounts.
 */
export function useApplyBrand(): void {
  const brand = useBrandQuery();
  const colour = brand.data?.primary_color ?? null;

  useEffect(() => {
    applyBrand(colour);
    return () => clearBrand();
  }, [colour]);
}

/** The one `<link rel="icon">` `index.html` declares. Mutated and restored rather than joined by a
 * second element: two icon links leave the choice to the browser's best-size heuristics, which is a
 * state with no single answer. */
const FAVICON_SELECTOR = 'link[rel="icon"]';

function restoreAttribute(link: HTMLLinkElement, name: string, value: string | null): void {
  if (value === null) link.removeAttribute(name);
  else link.setAttribute(name, value);
}

/** Wear the organisation's logo in the browser tab for as long as this is mounted (POS2-1149).
 *
 * Mounted beside `useApplyBrand`, for its reason: the organisation is unknown before
 * authentication, so `/login` keeps the FoodSight mark and a per-organisation favicon before
 * sign-in would need a per-org domain or a stored org hint — and a hint leaks tenant identity on a
 * shared machine. The icon therefore appears once the brand query resolves rather than on first
 * paint, which is inherent to one domain serving every tenant.
 *
 * `type` moves with `href` because the default is an SVG and the replacement is a PNG, so leaving
 * it would make the attribute lie. Both are read with `getAttribute`, so the restore writes back
 * what the document declared rather than the absolutised form `link.href` reports.
 */
export function useApplyFavicon(): void {
  const brand = useBrandQuery();
  const logoUrl = brand.data?.logo_url ?? null;

  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>(FAVICON_SELECTOR);
    if (!link || !logoUrl) return;

    const href = link.getAttribute("href");
    const type = link.getAttribute("type");
    let cancelled = false;

    void renderLogoFavicon(logoUrl).then((dataUrl) => {
      // A logo that cannot be rendered — including the tainted-canvas case POS2-1145 left live for
      // `foodsight-local` — leaves the FoodSight mark rather than clearing the icon.
      if (cancelled || !dataUrl) return;
      link.setAttribute("href", dataUrl);
      link.setAttribute("type", "image/png");
    });

    return () => {
      cancelled = true;
      restoreAttribute(link, "href", href);
      restoreAttribute(link, "type", type);
    };
  }, [logoUrl]);
}
